import './WatchStrapViewer.less';
import React, {useState, useEffect, useRef} from 'react';
import Images from '../../../models/image/Images';
import AspectContainer from '../../../components/aspect-container/AspectContainer';
import {projectionToVector} from '../../../utils/transform';


export default function WatchStrapViewer({watchCandidate, strapCandidate, images, zoomScale = 1}) {

    const watchImage = images.find(t => watchCandidate.layer_image_id === t.id);
    const upperImage = images.find(t => strapCandidate.upper_image_id === t.id);
    const lowerImage = images.find(t => strapCandidate.lower_image_id === t.id);

    const RENDER_AREA_WIDTH = 300;

    const frameRef = useRef(null);

    const calcMmPixels = () => {
        const frame = frameRef.current;

        if (frame === null) {
            return 1;
        }

        return frame.clientWidth / RENDER_AREA_WIDTH;
    };

    const [mmPixels, setMmPixels] = useState(calcMmPixels());

    const refreshMmPixels = () => {
        setMmPixels(calcMmPixels());
    };

    setTimeout(() => {
        refreshMmPixels();
    });

    useEffect(() => {
        refreshMmPixels();
    }, [frameRef]);

    useEffect(() => {
        const listener = () => refreshMmPixels();

        window.addEventListener('resize', listener);

        return () => window.removeEventListener('resize', listener);
    }, []);

    const mm2px = (mm) => {
        return mm * mmPixels;
    };

    const {
        x: wrist_translate_x,
        y: wrist_translate_y
    } = projectionToVector(
        watchCandidate.layer_position_x,
        watchCandidate.layer_position_y,
        watchCandidate.layer_angle * Math.PI / 180
    );

    const {
        x: upper_translate_x,
        y: upper_translate_y
    } = projectionToVector(
        strapCandidate.upper_position_x,
        strapCandidate.upper_position_y,
        strapCandidate.upper_angle * Math.PI / 180
    );

    const {
        x: lower_translate_x,
        y: lower_translate_y
    } = projectionToVector(
        strapCandidate.lower_position_x,
        strapCandidate.lower_position_y,
        strapCandidate.lower_angle * Math.PI / 180
    );

    const cssFilter = [
        `drop-shadow(0 0 ${mm2px(watchCandidate.lug_width / 5 * zoomScale)}px rgba(0,0,0,0.5))`
    ].join(' ');

    return <div className="watch-strap-viewer">
        <AspectContainer ratio={1}>
            <div className="preview" ref={frameRef}>
                <img
                    key={upperImage.id}
                    className="strap-image"
                    src={Images.getUrl(upperImage)}
                    width="100%"
                    height="auto"
                    style={{
                        bottom: `calc(50% + ${mm2px(watchCandidate.case_diameter / 2 + watchCandidate.strap_margin) * zoomScale}px)`,
                        transformOrigin: `50% 100%`,
                        transform: [
                            `scale(${zoomScale})`,
                            `scaleX(${strapCandidate.upper_scale_x / 100})`,
                            `scaleY(${strapCandidate.upper_scale_y / 100})`,
                            `rotate(${strapCandidate.upper_angle}deg)`,
                            `translateX(${mm2px(upper_translate_x)}px)`,
                            `translateY(${mm2px(upper_translate_y)}px)`,
                            `scale(${(strapCandidate.upper_width / RENDER_AREA_WIDTH)})`,
                        ].join(' '),
                        filter: cssFilter
                    }}
                    onLoad={() => refreshMmPixels()}
                />

                <img
                    key={lowerImage.id}
                    className="strap-image"
                    src={Images.getUrl(lowerImage)}
                    width="100%"
                    height="auto"
                    style={{
                        top: `calc(50% + ${mm2px(watchCandidate.case_diameter / 2 + watchCandidate.strap_margin) * zoomScale}px)`,
                        transformOrigin: `50% 0%`,
                        transform: [
                            `scale(${zoomScale})`,
                            `scaleX(${strapCandidate.lower_scale_x / 100})`,
                            `scaleY(${strapCandidate.lower_scale_y / 100})`,
                            `rotate(${strapCandidate.lower_angle}deg)`,
                            `translateX(${mm2px(lower_translate_x)}px)`,
                            `translateY(${mm2px(lower_translate_y)}px)`,
                            `scale(${(strapCandidate.lower_width / RENDER_AREA_WIDTH)})`,
                        ].join(' '),
                        filter: cssFilter
                    }}
                    onLoad={() => refreshMmPixels()}
                />

                <img
                    key={watchImage.id}
                    className="watch-image"
                    src={Images.getUrl(watchImage)}
                    width="100%"
                    height="auto"
                    style={{
                        transformOrigin: '50% 50%',
                        transform: [
                            `scale(${zoomScale})`,
                            `scale(${(watchCandidate.layer_width / RENDER_AREA_WIDTH)})`,
                            `scaleX(${watchCandidate.layer_scale_x / 100})`,
                            `scaleY(${watchCandidate.layer_scale_y / 100})`,
                            `rotate(${watchCandidate.layer_angle}deg)`,
                            `translateX(${mm2px(wrist_translate_x) / (watchCandidate.layer_width / RENDER_AREA_WIDTH)}px)`,
                            `translateY(${mm2px(wrist_translate_y) / (watchCandidate.layer_width / RENDER_AREA_WIDTH)}px)`,
                        ].join(' '),
                        filter: cssFilter
                    }}
                    onLoad={() => refreshMmPixels()}
                />
            </div>
        </AspectContainer>
    </div>;
}
